<template>
  <v-container fluid pa-0>
    <v-row>
      <!-- Date range -->
      <v-col cols="12">
        <helpers-section-sub-header>Date range:</helpers-section-sub-header>
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <v-menu :close-on-content-click="false" transition="scale-transition" min-width="290px">
              <template #activator="{ props }">
                <v-text-field
                  v-model="criteria.general.start_date"
                  label="Start date"
                  :prepend-icon="mdiCalendar"
                  hide-details
                  readonly
                  variant="outlined"
                  density="compact"
                  v-bind="props"
                  @change="criteria.general.start_date = $event.toISOString().slice(0, 10)"
                />
              </template>
              <v-date-picker v-model="criteria.general.start_date" no-title scrollable />
            </v-menu>
            
          </v-col>

          <v-col cols="12" sm="6" md="4" xl="3">
            <v-menu :close-on-content-click="false" transition="scale-transition" min-width="290px">
              <template #activator="{ props }">
                <v-text-field
                  v-model="criteria.general.end_date"
                  placeholder=" "
                  label="End date (optional)"
                  :prepend-icon="mdiCalendar"
                  hide-details
                  clearable
                  readonly
                  variant="outlined"
                  density="compact"
                  v-bind="props"
                />
              </template>
              <v-date-picker v-model="criteria.general.end_date" no-title scrollable />
            </v-menu>
          </v-col>
        </v-row>
      </v-col>

      <!-- Starting amount -->
      <v-col cols="12">
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <helpers-section-sub-header>Starting amount:</helpers-section-sub-header>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="criteria.general.starting_amount"
                  type="number"
                  prefix="$"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="pt-0"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>

      <!-- Starting amount -->
      <v-col cols="12">
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <helpers-section-sub-header>Performance Comparison:</helpers-section-sub-header>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="criteria.general.comp_index"
                  v-model:search-input="searchInput"
                  :items="searchResults"
                  :loading="loading"
                  :hide-no-data="!searchInput || searchInput.length < 1 || loading"
                  no-data-text="No such stock found"
                  return-object
                  item-title="description"
                  item-value="id"
                  variant="outlined"
                  density="compact"
                  :prepend-inner-icon="mdiMagnify"
                  hide-details
                  class="pt-0"
                  label="Search for comparison stock"
                >
                  <template #selection></template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <span v-if="!criteria.general.comp_index">
              {{ 'No comparison stock/index selected' }}
            </span>

            <template v-else>
              <v-chip
                small
                label
                close
                color="primary text-white"
                class="ma-1"
                @update:active="criteria.general.comp_index = null"
              >
                {{ criteria.general.comp_index.description }}
              </v-chip>
            </template>
          </v-col>
        </v-row>
      </v-col>

      <!-- Shorting Margin Options -->
      <v-col v-if="short" cols="12">
        <helpers-section-sub-header>Shorting Margin Options:</helpers-section-sub-header>
        <v-row>
          <v-col cols="12" sm="6" md="4" xl="3">
            <v-text-field
              v-model="criteria.general.short_maintenance_margin"
              type="number"
              variant="outlined"
              density="compact"
              label="Maintenance Margin %"
              suffix="%"
              hide-details
              class="pt-0"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12">
        <helpers-section-sub-header>E-mail notifications</helpers-section-sub-header>
        <v-checkbox
          v-model="current_trading_model.alert_enabled"
          density="compact"
          label="Send daily email with updated performance and trades"
          hide-details
        />
        <p>
          Email alerts will be sent out each trading morning. WhaleWisdom.com makes no guarantee for
          delivery of email alerts.
        </p>
      </v-col>
      <!--
      <v-col cols="12">
        <helpers-section-sub-header>13F Backtester Override</helpers-section-sub-header>
        <v-checkbox
          v-model="criteria.f13_override"
          density="compact"
          label="13F Override"
          hide-details
        />
      </v-col>
      -->
    </v-row>
    <!-- Slippage % Rate -->
    <helpers-section-sub-header class="mt-6"> Slippage % Rate: </helpers-section-sub-header>
    <p>
      When slippage is enabled for entries, the stock price used on entry will be calculated by
      taking (the high for the day minus the closing price) X the slippage rate and adding that
      value from the closing price.
    </p>
    <v-row no-gutters>
      <v-checkbox
        v-model="criteria.general.slippage_enabled"
        hide-details
        density="compact"
        class="shrink mr-2"
      />
      <v-text-field
        v-model="criteria.general.slippage_rate"
        :disabled="!criteria.general.slippage_enabled"
        type="number"
        suffix="%"
        variant="outlined"
        density="compact"
        class="shrink"
        hide-details
      />
      <v-col cols="12" class="mt-6">
        <h4 class="mb-2">Example:</h4>
        <div class="mb-1">Slippage rate: <code>25%</code></div>
        <div class="mb-1">Low price for day: <code>98</code></div>
        <div class="mb-1">Closing price for day: <code>100</code></div>
        <div class="mb-1">Factor: <code>(100-98) x 0.25 = 0.50</code></div>
        <div class="mb-1">Stock Price used in backtest: <code>$100 - 0.50 = $99.50</code></div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Modules
import { mapFields } from '@/store/helpers'
import AccessMixin from '@/mixins/access'
import api from '@/store/api'
import { mdiCalendar, mdiMagnify } from '@mdi/js'
export default {
  mixins: [AccessMixin],
  data() {
    return {
      mdiCalendar,
      mdiMagnify,
      short: false,
      loading: false,
      // stocksProxy: null,
      searchInput: null,
      searchResults: [],
      spy_tr_index: {
        name: 'S&P 500 Total Return Index',
        id: '169819',
        permalink: 'spyb',
        ticker: ['SP500TR'],
        rectype: 'stock',
        description: 'S&P 500 Total Return Index'
      }
    }
  },
  computed: {
    ...mapFields({
      criteria: 'app.whaleTraderCriteria',
      current_trading_model: 'app.current_trading_model'
    })
  },
  watch: {
    searchInput(val) {
      val && this.querySearch(val)
      this.searchInput = null
    }

    /* stocksProxy (stocksArray) {
          this.$emit('input',stocksArray);
        //this.$emit('update:stocks', stocksArray)
      } */
  },
  created() {
    this.searchResults.push(this.spy_tr_index)
    //   this.stocksProxy=this.criteria.general.comp_index;
  },
  methods: {
    formatName(item) {
      if (!item) {
        return ''
      }
      return item.description
    },
    matchall(val) {
      return true
    },
    querySearch(val) {
      this.loading = true
      const query = `stock_autocomplete?restrictions=${this.filterInput}&keyword=${val}`
      api
        .get(query)
        .then(res => {
          this.searchResults = []
          for (var i = 0; i < res.data.length; i++) {
            var item = res.data[i]
            this.searchResults.push({ id: item.id, description: item.ticker[0] + ' ' + item.name })
          }
          // this.searchResults = res.data
          this.searchResults.push(this.spy_tr_index)
        })
        .then(() => (this.loading = false))
    }
  }
}
</script>
